import { useState, useRef, useEffect } from 'react'
import ReactECharts from 'echarts-for-react'
import { useSelector } from 'react-redux'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFloppyDisk,
  faBookmark,
  faChartColumn,
  faChartBar,
  faTrash,
  faDownload,
  faPen,
  faTableList
} from '@fortawesome/pro-solid-svg-icons'
import {
  aiChat,
  aiSaveChart,
  aiListCharts,
  aiGetChart,
  aiRenameChart,
  aiDeleteChart,
  aiSchema
} from '../../../../services/analytics/analytics.service'
import AiInsights from './AiInsights'

// MF-506 — Maestro AI Analyst chat (data analysis + charts). Access is permission-driven
// (module 'AI-Analyst' → key 'ai-analyst'), NOT a hardcoded role. The backend does
// text→spec→SQL→DuckDB→chart; here we render the AgentResult inline (clarify | chart | error),
// and let the user SAVE a chart to their history (recall later).
const SUGGESTIONS = [
  'How many students by gender?',
  'Students by year level',
  'Average PAT scale score by year level'
]

const fmt = (v) => {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1)
  return String(v ?? '')
}

const escapeHtml = (s) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  )

// Tabla HTML simple para el export imprimible (PDF vía "Save as PDF" del navegador).
const tableHtml = (columns = [], rows = []) => {
  const head = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('')
  const body = rows
    .map((r) => `<tr>${columns.map((c) => `<td>${escapeHtml(fmt(r[c]))}</td>`).join('')}</tr>`)
    .join('')
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`
}

// Turns → messages for the API, keeping valid alternating roles (Bedrock requirement).
const toApiMessages = (turns) =>
  turns
    .filter((t) => t.role === 'user' || t.result)
    .map((t) =>
      t.role === 'user'
        ? { role: 'user', content: t.content }
        : { role: 'assistant', content: t.result?.type === 'clarify' ? t.result.question : '(result shown)' }
    )

const DataTable = ({ columns, rows }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #e0e0e0' }}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c} style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
                {fmt(r[c])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </Box>
)

// El backend manda la opción de ECharts como JSON → las FUNCIONES (formatters) NO sobreviven.
// Por eso el formateo de etiquetas y la orientación se hacen ACÁ, en el cliente.
const truncate = (s, n) => {
  const str = String(s ?? '')
  return str.length > n ? `${str.slice(0, n - 1)}…` : str
}

// Una fecha sin bucket llega como ISO con hora/zona (2026-01-25T00:00:00.000Z). Se muestra
// solo la fecha (2026-01-25). Si TODAS las categorías son ISO, se reescribe el array de datos
// (así también el tooltip queda limpio, no solo la etiqueta del eje). Los buckets ya vienen
// limpios ("2026-02", "2026-Q1") y no matchean, así que se dejan igual.
const ISO_DT = /^(\d{4}-\d{2}-\d{2})T/
const cleanDateCategories = (data = []) =>
  data.length && data.every((v) => ISO_DT.test(String(v)))
    ? data.map((v) => String(v).match(ISO_DT)[1])
    : null

// Con una sola serie la leyenda es redundante (título + eje ya la nombran) y encima se monta
// con el título (Few: menos tinta no-data). La quitamos para bar/line de una serie.
const dropRedundantLegend = (o) => {
  const series = o.series || []
  const isPie = series.some((s) => s.type === 'pie')
  if (!isPie && series.length <= 1) o.legend = undefined
  return o
}

// Trunca las etiquetas de un eje de categoría (vertical). El texto completo sigue en el tooltip
// (el axisPointer usa el valor real, no la etiqueta formateada).
const styleCategoryLabels = (option) => {
  const o = structuredClone(option)
  if (o.xAxis) {
    const cleaned = cleanDateCategories(o.xAxis.data)
    if (cleaned) o.xAxis.data = cleaned
    o.xAxis.axisLabel = { ...(o.xAxis.axisLabel || {}), formatter: (s) => truncate(s, 16), hideOverlap: true }
  }
  return dropRedundantLegend(o)
}

// Convierte una opción de barras VERTICAL en HORIZONTAL intercambiando eje categoría↔valor. Con
// nombres largos es lo recomendado (Stephen Few): cada nombre va en su propia fila, sin rotar, y
// la barra conserva su tamaño. El mayor queda arriba (viene ordenado desc → inverse).
const toHorizontalBar = (option) => {
  const o = structuredClone(option)
  const cat = o.xAxis || {}
  const val = o.yAxis || {}
  const catData = cleanDateCategories(cat.data || []) || cat.data || []
  o.xAxis = { type: 'value', name: val.name || '', nameLocation: 'middle', nameGap: 34 }
  o.yAxis = {
    type: 'category',
    data: catData,
    inverse: true,
    axisTick: { alignWithLabel: true },
    axisLabel: { formatter: (s) => truncate(s, 26) }
  }
  dropRedundantLegend(o)
  o.grid = { left: 12, right: 28, top: o.title ? (o.legend ? 44 : 30) : 16, bottom: 40, containLabel: true }
  if (o.legend) o.legend = { ...o.legend, top: o.title ? 22 : 2, bottom: undefined }
  if (Array.isArray(o.dataZoom)) {
    o.dataZoom = o.dataZoom.map((d) =>
      d.type === 'slider'
        ? {
            type: 'slider',
            yAxisIndex: 0,
            orient: 'vertical',
            right: 6,
            width: 14,
            start: d.start,
            end: d.end
          }
        : { type: 'inside', yAxisIndex: 0, start: d.start, end: d.end }
    )
  }
  return o
}

// Render de una gráfica (chart | table) como CARD diferenciada del chat: cabecera (pregunta +
// acciones), cuerpo (chart|table) y pie (nº de filas). Que sea una tarjeta con borde/cabecera
// hace obvio dónde termina la conversación y empieza el resultado generado.
const ChartView = ({ chart, rows, question, onSave }) => {
  const echartsRef = useRef(null)
  const isTable = chart.type === 'table'
  const title = chart?.option?.title?.text || (isTable ? 'Results' : 'Chart')
  const rowCount = isTable ? chart.rows?.length : rows?.length

  const isBarChart =
    !isTable && Array.isArray(chart.option?.series) && chart.option.series.some((s) => s.type === 'bar')
  const isCategoryChart = !isTable && chart.option?.xAxis?.type === 'category'
  const categories = chart.option?.xAxis?.data || []
  // Default horizontal cuando los nombres son largos o hay muchos (Few) → legible sin rotar.
  const longLabels = categories.some((c) => String(c).length > 12) || categories.length > 12
  const [horizontal, setHorizontal] = useState(isBarChart && longLabels)

  const useHorizontal = isBarChart && horizontal
  const option = useHorizontal
    ? toHorizontalBar(chart.option)
    : isCategoryChart
      ? styleCategoryLabels(chart.option)
      : chart.option
  // En horizontal damos ~26px por barra (hasta 560) para que se lean; vertical fijo 360.
  const chartHeight = useHorizontal ? Math.min(560, Math.max(320, categories.length * 26)) : 360

  const download = () => {
    let body = `<h3>${escapeHtml(title)}</h3>`
    if (isTable) {
      body += tableHtml(chart.columns, chart.rows)
    } else if (echartsRef.current?.getEchartsInstance) {
      const url = echartsRef.current
        .getEchartsInstance()
        .getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
      body += `<img src="${url}" style="max-width:100%"/>`
    }
    const w = window.open('', '_blank', 'width=920,height=680')
    if (!w) return
    w.document.write(
      `<html><head><title>${escapeHtml(title)}</title>` +
        '<style>body{font-family:Arial,sans-serif;padding:24px}table{border-collapse:collapse;width:100%;font-size:13px}' +
        'th,td{border:1px solid #ddd;padding:6px 10px;text-align:left}h3{color:#2b5784}</style></head>' +
        `<body onload="setTimeout(function(){window.print()},200)">${body}</body></html>`
    )
    w.document.close()
  }

  return (
    <Paper
      variant='outlined'
      sx={{ alignSelf: 'flex-start', width: '100%', borderRadius: 2, overflow: 'hidden' }}
    >
      {/* Cabecera: qué preguntó el usuario + acciones */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <FontAwesomeIcon icon={isTable ? faTableList : faChartColumn} color='#2b5784' />
        <Typography variant='caption' color='textSecondary' noWrap sx={{ flex: 1, minWidth: 0 }}>
          {question || title}
        </Typography>
        {isBarChart && (
          <Tooltip title={horizontal ? 'Vertical bars' : 'Horizontal bars'}>
            <IconButton size='small' onClick={() => setHorizontal((h) => !h)}>
              <FontAwesomeIcon icon={horizontal ? faChartColumn : faChartBar} size='xs' />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title='Download / print'>
          <IconButton size='small' onClick={download}>
            <FontAwesomeIcon icon={faDownload} size='xs' />
          </IconButton>
        </Tooltip>
        {onSave && (
          <Tooltip title='Save to your charts'>
            <IconButton size='small' onClick={onSave}>
              <FontAwesomeIcon icon={faFloppyDisk} size='xs' />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Cuerpo */}
      <Box sx={{ p: isTable ? 1 : 0.5 }}>
        {isTable ? (
          <DataTable columns={chart.columns} rows={chart.rows} />
        ) : (
          <ReactECharts
            ref={echartsRef}
            option={option}
            style={{ height: chartHeight, width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge
            lazyUpdate
          />
        )}
      </Box>

      {/* Pie: nº de filas */}
      {rowCount ? (
        <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant='caption' color='textSecondary'>
            {rowCount} rows
          </Typography>
        </Box>
      ) : null}
    </Paper>
  )
}

const AssistantResult = ({ result, question, onOption, onSave }) => {
  if (!result) return null

  if (result.type === 'clarify') {
    return (
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '90%' }}>
        <Typography sx={{ mb: 1 }}>{result.question}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(result.options || []).map((opt) => (
            <Chip
              key={opt}
              label={opt}
              onClick={() => onOption(opt)}
              clickable
              color='primary'
              variant='outlined'
            />
          ))}
        </Box>
      </Box>
    )
  }

  if (result.type === 'error') {
    return (
      <Alert severity='warning' sx={{ alignSelf: 'flex-start' }}>
        {(result.errors || ["I couldn't process that. Try rephrasing your question."]).join(' · ')}
      </Alert>
    )
  }

  if (result.type === 'chart') {
    return <ChartView chart={result.chart} rows={result.rows} question={question} onSave={onSave} />
  }

  return null
}

const AiAnalystChat = () => {
  const canUse = useSelector((state) => state?.session?.permissions?.['ai-analyst'])
  // Insights = analítica interna cross-usuario del tenant → gate Maestro (más restrictivo que
  // el permiso ai-analyst, que a futuro puede otorgarse a staff). TODO: permiso propio si escala.
  const userRoles = useSelector((state) => state?.session?.user?.roles || [])
  const isMaestro = userRoles.some((r) => ['Maestro'].includes(r?.name || r?.id))
  const [turns, setTurns] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('chat') // 'chat' | 'saved'
  const [saved, setSaved] = useState([])
  const [savedLoading, setSavedLoading] = useState(false)
  const [savedFilter, setSavedFilter] = useState('')
  const [savedSort, setSavedSort] = useState('newest')
  const [savedFrom, setSavedFrom] = useState('')
  const [notice, setNotice] = useState(null)
  const [schemaOpen, setSchemaOpen] = useState(false)
  const [schema, setSchema] = useState([])
  const [schemaLoading, setSchemaLoading] = useState(false)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  const dateOf = (s) => new Date(s.updatedAt || s.createdAt).getTime()
  const filteredSaved = saved
    .filter(
      (s) =>
        !savedFilter || `${s.label} ${s.question || ''}`.toLowerCase().includes(savedFilter.toLowerCase())
    )
    .filter((s) => !savedFrom || new Date(s.createdAt) >= new Date(savedFrom))
    .sort((a, b) => {
      if (savedSort === 'title') return String(a.label).localeCompare(String(b.label))
      return savedSort === 'oldest' ? dateOf(a) - dateOf(b) : dateOf(b) - dateOf(a)
    })

  useEffect(() => {
    if (view === 'chat' && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [turns, loading, view])

  // Al entrar (y al volver al chat) el input toma el foco para escribir de una.
  useEffect(() => {
    if (view === 'chat' && !loading) inputRef.current?.focus()
  }, [view, loading])

  if (!canUse) return null

  const openSchema = async () => {
    setSchemaOpen(true)
    if (schema.length) return
    setSchemaLoading(true)
    try {
      const res = await aiSchema()
      setSchema(res?.data || [])
    } finally {
      setSchemaLoading(false)
    }
  }

  const send = async (text) => {
    const content = (text ?? input).trim()
    if (!content || loading) return
    const history = [...turns, { role: 'user', content }]
    setTurns(history)
    setInput('')
    setLoading(true)
    try {
      // request (axios) tiene un interceptor que ya desenvuelve `response.data`, así que
      // `res` ES el body `{ data: AgentResult }`. El AgentResult es `res.data` (un solo .data).
      const res = await aiChat(toApiMessages(history))
      const data = res?.data
      setTurns([...history, { role: 'assistant', result: data, question: content }])
    } catch (e) {
      const msg = e?.error || e?.message || 'Request failed'
      setTurns([...history, { role: 'assistant', result: { type: 'error', errors: [msg] } }])
    } finally {
      setLoading(false)
    }
  }

  const loadSaved = async () => {
    setSavedLoading(true)
    try {
      const res = await aiListCharts()
      setSaved(res?.data || [])
    } finally {
      setSavedLoading(false)
    }
  }

  const openSaved = () => {
    setView('saved')
    loadSaved()
  }

  const handleSave = async (turn) => {
    if (turn?.result?.type !== 'chart') return
    // eslint-disable-next-line no-alert
    const label = window.prompt('Name this chart:', turn.question || 'My chart')
    if (!label || !label.trim()) return
    try {
      await aiSaveChart({ label: label.trim(), question: turn.question || '', chart: turn.result.chart })
      setNotice({ severity: 'success', msg: `Saved “${label.trim()}” to your history.` })
    } catch {
      setNotice({ severity: 'error', msg: 'Could not save the chart.' })
    }
  }

  const recall = async (id) => {
    try {
      const res = await aiGetChart(id)
      const doc = res?.data
      if (!doc?.chart) return
      setTurns((t) => [
        ...t,
        { role: 'user', content: `★ ${doc.label}` },
        { role: 'assistant', result: { type: 'chart', chart: doc.chart, rows: [] }, question: doc.question }
      ])
      setView('chat')
    } catch {
      setNotice({ severity: 'error', msg: 'Could not open the saved chart.' })
    }
  }

  const remove = async (id) => {
    try {
      await aiDeleteChart(id)
      setSaved((s) => s.filter((x) => x._id !== id))
    } catch {
      setNotice({ severity: 'error', msg: 'Could not delete the chart.' })
    }
  }

  const rename = async (item) => {
    // eslint-disable-next-line no-alert
    const label = window.prompt('Rename chart:', item.label)
    if (!label || !label.trim() || label.trim() === item.label) return
    try {
      await aiRenameChart(item._id, label.trim())
      setSaved((s) => s.map((x) => (x._id === item._id ? { ...x, label: label.trim() } : x)))
    } catch {
      setNotice({ severity: 'error', msg: 'Could not rename the chart.' })
    }
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        m: 3,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        // Llenar el contenedor real (BodyFullContainer es h-full acotado por CSS) en vez de un
        // número mágico calc(100vh-…) que no cuaja con la altura disponible y rompe el scroll
        // interno en pantallas chicas (misma lección que MF-496). Se RESTAN los 48px del margen
        // `m: 3` (24px arriba + 24px abajo): con height:100% + margen, el Paper desbordaba el
        // contenedor y el overflow-hidden del módulo recortaba el input de abajo.
        height: 'calc(100% - 48px)',
        minHeight: 460,
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant='h6' color='primary'>
            Maestro AI Analyst
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            Ask about your data (internal beta). E.g. students by gender, PAT averages, enrolments by subject.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            size='small'
            variant='outlined'
            startIcon={<FontAwesomeIcon icon={faTableList} />}
            onClick={openSchema}
          >
            Data
          </Button>
          <Button
            size='small'
            variant={view === 'saved' ? 'contained' : 'outlined'}
            startIcon={<FontAwesomeIcon icon={faBookmark} />}
            onClick={() => (view === 'saved' ? setView('chat') : openSaved())}
          >
            Saved
          </Button>
          {isMaestro && (
            <Button
              size='small'
              variant={view === 'insights' ? 'contained' : 'outlined'}
              startIcon={<FontAwesomeIcon icon={faChartColumn} />}
              onClick={() => setView(view === 'insights' ? 'chat' : 'insights')}
            >
              Insights
            </Button>
          )}
        </Box>
      </Box>

      {notice && (
        <Alert severity={notice.severity} sx={{ mt: 1 }} onClose={() => setNotice(null)}>
          {notice.msg}
        </Alert>
      )}

      {view === 'saved' ? (
        <Box sx={{ flex: 1, minHeight: 0, my: 1.5, display: 'flex', flexDirection: 'column' }}>
          {saved.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexShrink: 0, flexWrap: 'wrap' }}>
              <TextField
                size='small'
                placeholder='Search by title or question…'
                value={savedFilter}
                onChange={(e) => setSavedFilter(e.target.value)}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <TextField
                size='small'
                select
                label='Sort'
                value={savedSort}
                onChange={(e) => setSavedSort(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value='newest'>Newest</MenuItem>
                <MenuItem value='oldest'>Oldest</MenuItem>
                <MenuItem value='title'>Title A–Z</MenuItem>
              </TextField>
              <TextField
                size='small'
                type='date'
                label='From'
                InputLabelProps={{ shrink: true }}
                value={savedFrom}
                onChange={(e) => setSavedFrom(e.target.value)}
                sx={{ minWidth: 150 }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {savedLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} /> <Typography variant='body2'>Loading…</Typography>
              </Box>
            ) : saved.length === 0 ? (
              <Typography variant='body2' color='textSecondary'>
                No saved charts yet. Generate a chart and click “Save”.
              </Typography>
            ) : filteredSaved.length === 0 ? (
              <Typography variant='body2' color='textSecondary'>
                No saved charts match “{savedFilter}”.
              </Typography>
            ) : (
              filteredSaved.map((s) => (
                <Box
                  key={s._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography noWrap sx={{ fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                    <Typography variant='caption' color='textSecondary' noWrap display='block'>
                      {s.question ? `${s.question} · ` : ''}
                      {new Date(s.updatedAt || s.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    <Button size='small' onClick={() => recall(s._id)}>
                      Open
                    </Button>
                    <IconButton size='small' onClick={() => rename(s)} title='Rename'>
                      <FontAwesomeIcon icon={faPen} size='xs' />
                    </IconButton>
                    <IconButton size='small' color='error' onClick={() => remove(s._id)} title='Delete'>
                      <FontAwesomeIcon icon={faTrash} size='xs' />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      ) : view === 'insights' ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', my: 1.5 }}>
          <AiInsights />
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            my: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            // Los hijos (burbujas + cards) NO deben encogerse: en una columna flex el
            // flex-shrink por defecto (1) aplasta las cards para que quepan en vez de hacer
            // scroll → el chart de altura fija se recorta. Con shrink:0, la columna desborda
            // y el contenedor (overflowY:auto) hace scroll correctamente.
            '& > *': { flexShrink: 0 }
          }}
        >
          {turns.map((t, i) =>
            t.role === 'user' ? (
              <Box
                key={i}
                sx={{
                  alignSelf: 'flex-end',
                  bgcolor: 'primary.main',
                  color: '#fff',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  maxWidth: '80%',
                  fontSize: 14,
                  wordBreak: 'break-word'
                }}
              >
                {t.content}
              </Box>
            ) : (
              <AssistantResult
                key={i}
                result={t.result}
                question={t.question}
                onOption={(opt) => send(opt)}
                onSave={t.result?.type === 'chart' ? () => handleSave(t) : undefined}
              />
            )
          )}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} /> <Typography variant='body2'>Analyzing…</Typography>
            </Box>
          )}

          {turns.length === 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {SUGGESTIONS.map((s) => (
                <Chip key={s} label={s} size='small' variant='outlined' onClick={() => send(s)} clickable />
              ))}
            </Box>
          )}
        </Box>
      )}

      {view === 'chat' && (
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, mt: 0.5, mb: 0.5 }}>
          <TextField
            fullWidth
            size='small'
            inputRef={inputRef}
            autoFocus
            autoComplete='off'
            inputProps={{ autoComplete: 'off' }}
            placeholder='Type your question…'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send()
              }
            }}
            disabled={loading}
          />
          <Button variant='contained' onClick={() => send()} disabled={loading || !input.trim()}>
            Send
          </Button>
        </Box>
      )}

      <Dialog open={schemaOpen} onClose={() => setSchemaOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Available data</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
            The assistant can only query the data below (what we process into analytics). It does not have
            access to everything in the app.
          </Typography>
          {schemaLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} /> <Typography variant='body2'>Loading…</Typography>
            </Box>
          ) : (
            schema.map((t) => (
              <Box key={t.name} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {t.name.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())}
                </Typography>
                <Typography variant='caption' color='textSecondary' display='block'>
                  {t.description}
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(t.columns || []).map((c) => (
                    <Chip key={c.name} size='small' variant='outlined' label={c.name} title={c.description} />
                  ))}
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  )
}

export default AiAnalystChat
